import { some, startCase } from "lodash";
import { preventDefaultMouseEvent } from "@app/shared/utility/preventDefaultMouseEvent";
import { ResultRegion } from "@app/projects/results/common";
import { SpecialtyGroupExceptionHistoryTimeline } from "@app/projects/network-assessment/SpecialtyGroupExceptionHistoryTimeline";
import { FlyoutLink } from "@app/shared/Flyouts";
import moment from "moment";
import ExceptionFlyout from "@app/projects/network-assessment/SpecialtyGroupExceptionFlyout";
import { SpecialtyGroupException } from "@app/exceptions/interfaces";
import { SpecialtyGroupExceptionStatus } from "@app/exceptions/enums";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { formatDateTime } from "@app/shared/formatDateTime";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { FC, MouseEvent } from "react";

interface SpecialtyGroupExceptionEditorProps {
  customProjectId: number;
  editEnabled: boolean;
  region: ResultRegion;
  specialtyGroupCode: string;
  specialtyGroupName: string;
  exception: SpecialtyGroupException;
  onSubmit: (exception: SpecialtyGroupException) => void;
}

const SpecialtyGroupExceptionEditor: FC<SpecialtyGroupExceptionEditorProps> = (props: SpecialtyGroupExceptionEditorProps) => {
  const renderException = () => {
    if (!props.exception) {
      return null;
    }

    const status = startCase(props.exception.specialtyGroupExceptionStatus);
    const date = formatDateTime(props.exception.dateUpdated);
    const startDate = props.exception.startDate ? moment(props.exception.startDate).format("MM/DD/YYYY") : '---';
    const endDate = props.exception.endDate ? moment(props.exception.endDate).format("MM/DD/YYYY") : '---';
    const previousApproval = props.exception.previouslyApproved ? "Previously Approved" : "NOT Previously Approved";
    const comment = props.exception.comment;
    const exceptionNarrative = props.exception.exceptionNarrative;

    return (
      <div className="specialty-group-exception-editor-status">
        <div>
          <span className="pr10">Exception: <strong>{status}</strong>, {previousApproval}</span>
          <span> - </span>
          <span className="pl10 pr10">{date}</span>
          {shouldShowExceptionHistory() &&
            <span className="pull-right">
              <TooltipWrapper text="Show Exception Timeline Details" position="bottom">
                <FlyoutLink
                  classNames="specialty-history-link mt-2 text-dark p-0"
                  content={{
                    body: <SpecialtyGroupExceptionHistoryTimeline specialtyGroupException={props.exception} />,
                    title: "Specialty Exception History",
                  }}
                  size="40"
                  testId="specialty-group-exception-editor__exception-history-flyout"
                >
                  <QesIcon iconName={QesIcons.ClockRewind} description="Opens exception history flyout" />
                </FlyoutLink>
              </TooltipWrapper>
            </span>
          }
          {props.editEnabled &&
            (
              <RoleRestricted allowedRoles={[Roles.EXCEPTION_MANAGER]}>
                <span className="pull-right mr10">
                  <FlyoutLink
                    classNames="qes-button-secondary"
                    content={{
                      body: renderForm(),
                      title: "Specialty Exception Information",
                    }}
                    size="40"
                    testId="specialty-group-exception-editor__edit-exception-flyout"
                  >
                    Edit
                  </FlyoutLink>
                  <a className="qes-button qes-button-secondary" type="button" href="#" onClick={(e: MouseEvent) => preventDefaultMouseEvent(e, handleDelete)}>Archive</a>
                </span>
              </RoleRestricted>
            )
          }

        </div>
        <div>
          Processed: {props.exception.isUnprocessed ? 'No' : 'Yes'}
        </div>
        {startDate && startDate !== '' && startDate !== '---' && (
          <div className="text-muted pt5">
            Start Date: {startDate}
          </div>
        )}
        {endDate && endDate !== '' && endDate !== '---' && (
          <div className="text-muted pt5">
            Expiration Date: {endDate}
          </div>
        )}
        {comment && (
          <div className="text-muted pt5">
            Comment: {comment}
          </div>
        )}
        {exceptionNarrative && (
          <div className="text-muted pt5">
            Exception Narrative: {exceptionNarrative}
          </div>
        )}
      </div>
    );
  }

  const renderForm = () => {
    return (
      <ExceptionFlyout
        customProjectId={props.customProjectId}
        exception={props.exception}
        specialtyGroupName={props.specialtyGroupName}
        specialtyGroupCode={props.specialtyGroupCode}
        region={props.region}
        handleSubmit={handleSubmit}
      />
    );
  }

  const shouldShowException = () => {
    return props.exception
      && props.exception.specialtyGroupExceptionStatus
      && shouldShowStatus(props.exception.specialtyGroupExceptionStatus);
  }

  const shouldShowExceptionHistory = () => {
    return shouldShowException()
      && props.exception.specialtyGroupExceptionStatus !== SpecialtyGroupExceptionStatus.None
      && props.exception.specialtyGroupExceptionStatus !== SpecialtyGroupExceptionStatus.Archived;
  }

  const shouldShowStatus = (value: SpecialtyGroupExceptionStatus) => {
    const statuses = [
      SpecialtyGroupExceptionStatus.Pending,
      SpecialtyGroupExceptionStatus.Submitted,
      SpecialtyGroupExceptionStatus.Approved,
      SpecialtyGroupExceptionStatus.Denied,
      SpecialtyGroupExceptionStatus.Appealed,
    ];
    return some(statuses, (s) => s === value);
  }

  const handleSubmit = (exceptionToSubmit: SpecialtyGroupException) => {
    props.onSubmit(exceptionToSubmit);
  }

  const handleDelete = () => {
    const exception: SpecialtyGroupException = {
      ...props.exception,
      comment: "",
      dateUpdated: new Date(),
      exceptionNarrative: "",
      previouslyApproved: false,
      specialtyGroupExceptionStatus: SpecialtyGroupExceptionStatus.Archived,
    };
    props.onSubmit(exception);
  }

  return (
    <>
      {shouldShowException() ? (
        renderException()
      ) : props.editEnabled &&
      (
        <div className="specialty-group-exception-editor-trigger">
          <RoleRestricted allowedRoles={[Roles.EXCEPTION_MANAGER]}>
            <FlyoutLink
              classNames="qes-button-secondary"
              content={{
                body: renderForm(),
                title: "Specialty Exception Information",
              }}
              testId="specialty-group-exception-editor__enter-exception-flyout"
            >
              Enter an Exception
            </FlyoutLink>
          </RoleRestricted>
        </div>
      )
      }
    </>
  );
}

export default SpecialtyGroupExceptionEditor;
